import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import type { Story } from "../types";
import { accentForId, accentGradient } from "../utils/accent";
import { TagEditor } from "./TagEditor";

interface StoryCardProps {
	story: Story;
}

export function StoryCard({ story }: StoryCardProps) {
	const { t, i18n } = useTranslation();
	const accent = accentForId(story.id);
	const date = new Date(story.created_at).toLocaleDateString(i18n.language, {
		month: "long",
		day: "numeric",
		year: "numeric",
	});
	const storyHref = `/stories/${story.id}`;

	return (
		<article
			aria-label={story.title ?? t("stories.card.untitled")}
			className="w-full backdrop-blur-sm border border-ink/10 rounded-sm pl-5 pr-6 py-5 transition-all duration-200 hover:shadow-[0_4px_20px_rgba(26,18,9,0.1)] hover:-translate-y-0.5"
			style={{
				borderLeft: `3px solid ${accent}`,
				background: accentGradient(accent),
			}}
		>
			<Link to={storyHref} className="block">
				<div className="flex items-start justify-between gap-4">
					<h2 className="font-display text-xl font-bold text-ink leading-tight">
						{story.title ?? t("stories.card.untitled")}
					</h2>
					<span
						className="font-ui text-[10px] uppercase tracking-widest shrink-0 px-2 py-0.5 rounded-sm mt-1"
						style={{ color: accent, border: `1px solid ${accent}` }}
					>
						{t(`stories.card.status.${story.status}`)}
					</span>
				</div>
				<p className="font-ui text-[11px] uppercase tracking-widest text-ink/70 mt-1.5">
					{date}
				</p>
				{story.preview && (
					<p className="font-body italic text-sm text-ink/70 mt-2 line-clamp-2 leading-relaxed">
						{story.preview}
					</p>
				)}
			</Link>
			<div className="mt-2.5">
				<TagEditor
					storyId={story.id}
					tags={story.tags}
					invalidate={[["stories"]]}
				/>
			</div>
		</article>
	);
}
